import { useEffect, useState } from 'react';
import { Search, CalendarRange, ArrowUpDown, X } from 'lucide-react';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@components/ui/input-group';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@components/ui/popover';
import { Calendar } from '@components/ui/calendar';
import { Button } from '@components/ui/button';
import MultiSelect from '@generated/components/MultiSelect';
import { getFilterOptions } from '@generated/server/sessions';

export type DateRangeValue = { from?: Date; to?: Date };

export interface SessionFilters {
  search: string;
  subject: string;
  hostingSite: string[];
  gradeBand: string[];
  dateRange: DateRangeValue;
  sortBy: 'day1Date' | 'name' | 'hostingSite';
  sortDir: 'asc' | 'desc';
}

const GRADE_BAND_ORDER = ['K-2', '3-5', '6-8', '9-12'];

function sortGradeBands(bands: string[]) {
  return [...bands].sort((a, b) => {
    const ai = GRADE_BAND_ORDER.indexOf(a);
    const bi = GRADE_BAND_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export const defaultFilters: SessionFilters = {
  search: '',
  subject: 'all',
  hostingSite: [],
  gradeBand: [],
  dateRange: {},
  sortBy: 'day1Date',
  sortDir: 'asc',
};

function formatDate(d?: Date) {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SessionsToolbar({
  filters,
  onChange,
}: {
  filters: SessionFilters;
  onChange: (next: SessionFilters) => void;
}) {
  const [subjects, setSubjects] = useState<string[]>([]);
  const [hostingSites, setHostingSites] = useState<string[]>([]);
  const [gradeBands, setGradeBands] = useState<string[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(filters.search);

  useEffect(() => {
    let active = true;
    getFilterOptions({ data: {} })
      .then((res) => {
        if (!active) return;
        setSubjects(res?.subjects ?? []);
        setHostingSites(res?.hostingSites ?? []);
        setGradeBands(sortGradeBands(res?.gradeBands ?? []));
      })
      .catch((err) => {
        console.error('Failed to load filter options', err);
        if (!active) return;
        const message = err instanceof Error ? err.message : String(err);
        setOptionsError(
          message.includes('NOT_AUTHENTICATED')
            ? 'Filter options need you to be signed in to monday.com.'
            : 'Some filter options failed to load.',
        );
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchDraft !== filters.search) onChange({ ...filters, search: searchDraft });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const hasActiveFilters =
    filters.search ||
    filters.subject !== 'all' ||
    filters.hostingSite.length > 0 ||
    filters.gradeBand.length > 0 ||
    filters.dateRange.from ||
    filters.dateRange.to;

  const clearAll = () => {
    setSearchDraft('');
    onChange({ ...defaultFilters });
  };

  const rangeLabel =
    filters.dateRange.from || filters.dateRange.to
      ? `${formatDate(filters.dateRange.from)}${filters.dateRange.to ? ' – ' + formatDate(filters.dateRange.to) : ''}`
      : 'Any date';

  return (
    <div className="flex flex-col gap-3 border border-border bg-[hsl(var(--brand-red))] p-4">
      <InputGroup className="w-full bg-background sm:max-w-sm">
        <InputGroupAddon align="inline-start">
          <Search className="size-4 text-muted-foreground" aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          placeholder="Search session title…"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          aria-label="Search session title"
        />
      </InputGroup>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-center lg:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
        <MultiSelect
          label="Hosting site"
          options={hostingSites}
          selected={filters.hostingSite}
          onChange={(v) => onChange({ ...filters, hostingSite: v })}
        />

        <MultiSelect
          label="Grade band"
          options={gradeBands}
          selected={filters.gradeBand}
          onChange={(v) => onChange({ ...filters, gradeBand: v })}
        />

        <Select value={filters.subject} onValueChange={(v) => onChange({ ...filters, subject: v })}>
          <SelectTrigger className="w-full bg-background" aria-label="Filter by subject">
            <SelectValue placeholder="Subject" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subjects</SelectItem>
            {subjects.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start gap-2" aria-label="Filter by date range">
              <CalendarRange className="size-4" aria-hidden="true" />
              {rangeLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={{ from: filters.dateRange.from, to: filters.dateRange.to }}
              onSelect={(range) => onChange({ ...filters, dateRange: range ?? {} })}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>

        <Select
          value={`${filters.sortBy}:${filters.sortDir}`}
          onValueChange={(v) => {
            const [sortBy, sortDir] = v.split(':') as [SessionFilters['sortBy'], SessionFilters['sortDir']];
            onChange({ ...filters, sortBy, sortDir });
          }}
        >
          <SelectTrigger className="w-full bg-background lg:w-52" aria-label="Sort sessions">
            <ArrowUpDown className="size-3.5 text-muted-foreground" aria-hidden="true" />
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day1Date:asc">Date (soonest first)</SelectItem>
            <SelectItem value="day1Date:desc">Date (latest first)</SelectItem>
            <SelectItem value="name:asc">Title (A–Z)</SelectItem>
            <SelectItem value="name:desc">Title (Z–A)</SelectItem>
            <SelectItem value="hostingSite:asc">Hosting site (A–Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {hasActiveFilters ? (
        <div>
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={clearAll}>
            <X className="size-3.5" aria-hidden="true" />
            Clear filters
          </Button>
        </div>
      ) : null}

      {optionsError ? <p className="text-xs text-destructive">{optionsError}</p> : null}
    </div>
  );
}
